import type {
  AddEmergencyNoteInput,
  CancelFalseAlarmInput,
  EmergencyCommandOperation,
  EmergencyIncident,
  EmergencyIncidentFirestoreRecord,
  AssignPrimaryEmergencyResponderInput,
  ReassignEmergencyResponderInput,
  ResolveEmergencyInput,
  EmergencyAssignmentFirestoreRecord,
} from '@src/models/interfaces/emergency.interface';
import { EmergencyIncidentMapper } from '@src/models/mappers/EmergencyIncidentMapper';
import { DateUtil } from '@src/packages/utils/dates';
import { EmergencyAssignmentRepository } from '@src/repositories/EmergencyAssignmentRepository';
import { EmergencyIncidentRepository } from '@src/repositories/EmergencyIncidentRepository';

import { EmergencyParticipantService } from './EmergencyParticipantService';

export type EmergencyIncidentRepositoryPort = Pick<
  EmergencyIncidentRepository,
  | 'addNote'
  | 'cancelFalseAlarm'
  | 'get'
  | 'getCapabilities'
  | 'listActive'
  | 'listHistory'
  | 'reassign'
  | 'assignPrimary'
  | 'resolve'
  | 'subscribeActive'
  | 'subscribeOne'
>;
export type EmergencyAssignmentRepositoryPort = Pick<
  EmergencyAssignmentRepository,
  'list' | 'subscribe'
>;
export type EmergencyParticipantServicePort = Pick<EmergencyParticipantService, 'resolveNames'>;

export class EmergencyIncidentService {
  private readonly repository: EmergencyIncidentRepositoryPort;
  private readonly assignmentRepository: EmergencyAssignmentRepositoryPort;
  private readonly participantService: EmergencyParticipantServicePort;

  constructor(
    repository: EmergencyIncidentRepositoryPort = new EmergencyIncidentRepository(),
    assignmentRepository: EmergencyAssignmentRepositoryPort = new EmergencyAssignmentRepository(),
    participantService: EmergencyParticipantServicePort = new EmergencyParticipantService(),
  ) {
    this.repository = repository;
    this.assignmentRepository = assignmentRepository;
    this.participantService = participantService;
  }

  async listActive(
    organizationId: string,
    divisionIds: string[],
    organizationWide: boolean,
  ): Promise<EmergencyIncident[]> {
    this.requireOrganization(organizationId);
    const records = await this.repository.listActive(organizationId, divisionIds, organizationWide);
    const incidents = await Promise.all(
      records.map(async record => {
        const assignments = await this.assignmentRepository.list(organizationId, record.id);
        return EmergencyIncidentMapper.fromFirestore(record, assignments);
      }),
    );
    return this.resolveParticipantNames(organizationId, this.sort(incidents));
  }

  subscribeActive(
    organizationId: string,
    divisionIds: string[],
    organizationWide: boolean,
    onData: (incidents: EmergencyIncident[]) => void,
    onError: (error: Error) => void,
  ): () => void {
    this.requireOrganization(organizationId);
    const assignmentsByIncident = new Map<string, EmergencyAssignmentFirestoreRecord[]>();
    const assignmentUnsubscribes = new Map<string, () => void>();
    let incidentRecords: EmergencyIncidentFirestoreRecord[] = [];

    const emit = () => {
      const incidents = this.sort(
        incidentRecords.map(record =>
          EmergencyIncidentMapper.fromFirestore(
            record,
            assignmentsByIncident.get(record.id) ?? [],
          ),
        ),
      );
      void this.resolveParticipantNames(organizationId, incidents).then(onData).catch(onError);
    };

    const syncAssignmentSubscriptions = (records: EmergencyIncidentFirestoreRecord[]) => {
      const activeIds = new Set(records.map(record => record.id));
      assignmentUnsubscribes.forEach((unsubscribe, incidentId) => {
        if (activeIds.has(incidentId)) return;
        unsubscribe();
        assignmentUnsubscribes.delete(incidentId);
        assignmentsByIncident.delete(incidentId);
      });
      records.forEach(record => {
        if (assignmentUnsubscribes.has(record.id)) return;
        const unsubscribe = this.assignmentRepository.subscribe(
          organizationId,
          record.id,
          value => {
            assignmentsByIncident.set(record.id, value);
            emit();
          },
          onError,
        );
        assignmentUnsubscribes.set(record.id, unsubscribe);
      });
    };

    const unsubscribeIncidents = this.repository.subscribeActive(
      organizationId,
      divisionIds,
      organizationWide,
      records => {
        incidentRecords = records;
        syncAssignmentSubscriptions(records);
        emit();
      },
      onError,
    );

    return () => {
      unsubscribeIncidents();
      assignmentUnsubscribes.forEach(unsubscribe => unsubscribe());
    };
  }

  subscribeDetail(
    organizationId: string,
    incidentId: string,
    onData: (incident: EmergencyIncident | null) => void,
    onError: (error: Error) => void,
    onCapabilitiesError?: (error: Error) => void,
  ): () => void {
    this.requireOrganization(organizationId);
    let record: EmergencyIncidentFirestoreRecord | null = null;
    let assignments: EmergencyAssignmentFirestoreRecord[] = [];
    let recordLoaded = false;
    let assignmentsLoaded = false;
    let disposed = false;
    let emitGeneration = 0;
    let capabilitiesRequestId = 0;
    let capabilitiesKey: string | null = null;
    let inFlightCapabilitiesKey: string | null = null;
    let hasResolvedCapabilities = false;
    let allowedCommands: EmergencyCommandOperation[] = [];

    const emitIncident = async () => {
      if (disposed) return;
      if (!recordLoaded || !record) {
        if (recordLoaded) onData(null);
        return;
      }
      const requestId = ++emitGeneration;
      const incident = EmergencyIncidentMapper.fromFirestore(record, assignments);
      const [resolvedIncident] = await this.resolveParticipantNames(organizationId, [incident]);
      if (disposed || requestId !== emitGeneration || !hasResolvedCapabilities) return;
      resolvedIncident.allowedCommands = allowedCommands;
      onData(resolvedIncident);
    };

    const refreshCapabilitiesIfNeeded = async () => {
      if (disposed || !recordLoaded || !record) return;
      const nextKey = this.getDetailCapabilitiesKey(record, assignments);
      if (nextKey === capabilitiesKey || nextKey === inFlightCapabilitiesKey) return;
      const requestId = ++capabilitiesRequestId;
      inFlightCapabilitiesKey = nextKey;
      try {
        const commands = await this.repository.getCapabilities(
          organizationId,
          record.divisionId ?? '',
          incidentId,
        );
        if (disposed || requestId !== capabilitiesRequestId) return;
        allowedCommands = commands;
        capabilitiesKey = nextKey;
        inFlightCapabilitiesKey = null;
        hasResolvedCapabilities = true;
        await emitIncident();
      } catch (error) {
        if (disposed || requestId !== capabilitiesRequestId) return;
        allowedCommands = [];
        capabilitiesKey = nextKey;
        inFlightCapabilitiesKey = null;
        hasResolvedCapabilities = true;
        await emitIncident();
        onCapabilitiesError?.(
          error instanceof Error ? error : new Error('Unable to load emergency capabilities'),
        );
      }
    };

    const emitSafely = async () => {
      try {
        await emitIncident();
        if (!recordLoaded || !assignmentsLoaded) return;
        await refreshCapabilitiesIfNeeded();
      } catch (error) {
        onError(error instanceof Error ? error : new Error('Unable to load emergency capabilities'));
      }
    };
    const unsubscribeIncident = this.repository.subscribeOne(
      organizationId,
      incidentId,
      value => {
        record = value;
        recordLoaded = true;
        void emitSafely();
      },
      onError,
    );
    const unsubscribeAssignments = this.assignmentRepository.subscribe(
      organizationId,
      incidentId,
      value => {
        assignments = value;
        assignmentsLoaded = true;
        void emitSafely();
      },
      onError,
    );
    return () => {
      disposed = true;
      capabilitiesRequestId += 1;
      emitGeneration += 1;
      unsubscribeIncident();
      unsubscribeAssignments();
    };
  }

  async listHistory(
    organizationId: string,
    divisionIds: string[],
    organizationWide: boolean,
    options: {
      fromMs?: number | null;
      toMs?: number | null;
      limitCount?: number;
    } = {},
  ): Promise<EmergencyIncident[]> {
    this.requireOrganization(organizationId);
    const records = await this.repository.listHistory(
      organizationId,
      divisionIds,
      organizationWide,
      options,
    );
    return this.resolveParticipantNames(
      organizationId,
      this.sort(records.map(record => EmergencyIncidentMapper.fromFirestore(record))),
    );
  }

  async get(
    organizationId: string,
    incidentId: string,
    divisionIds: string[],
    organizationWide: boolean,
  ): Promise<EmergencyIncident | null> {
    this.requireOrganization(organizationId);
    if (!incidentId) throw new Error('Incident ID is required');
    const [record, assignments] = await Promise.all([
      this.repository.get(organizationId, incidentId, divisionIds, organizationWide),
      this.assignmentRepository.list(organizationId, incidentId),
    ]);
    if (!record) return null;
    const incident = EmergencyIncidentMapper.fromFirestore(record, assignments);
    const [resolvedIncident] = await this.resolveParticipantNames(organizationId, [incident]);
    try {
      resolvedIncident.allowedCommands = await this.repository.getCapabilities(
        organizationId,
        resolvedIncident.divisionId,
        incidentId,
      );
    } catch {
      resolvedIncident.allowedCommands = [];
    }
    return resolvedIncident;
  }

  async reassign(input: ReassignEmergencyResponderInput): Promise<void> {
    this.validateIncidentCommand(input);
    if (!input.assignmentId || !input.responderId) throw new Error('Responder is required');
    await this.repository.reassign(input);
  }

  async assignPrimary(input: AssignPrimaryEmergencyResponderInput): Promise<void> {
    this.validateIncidentCommand(input);
    if (!input.responderId) throw new Error('Responder is required');
    await this.repository.assignPrimary(input);
  }

  async addNote(input: AddEmergencyNoteInput): Promise<void> {
    this.validateIncidentCommand(input);
    if (input.text.trim().length < 3) throw new Error('Note is too short');
    await this.repository.addNote({ ...input, text: input.text.trim() });
  }

  async cancelFalseAlarm(input: CancelFalseAlarmInput): Promise<void> {
    this.validateIncidentCommand(input);
    if (!input.reason.trim()) throw new Error('False-alarm reason is required');
    await this.repository.cancelFalseAlarm({ ...input, reason: input.reason.trim() });
  }

  async resolve(input: ResolveEmergencyInput): Promise<void> {
    this.validateIncidentCommand(input);
    if (!input.reason.trim() || !input.outcome.trim()) {
      throw new Error('Resolution reason and outcome are required');
    }
    await this.repository.resolve({
      ...input,
      reason: input.reason.trim(),
      outcome: input.outcome.trim(),
    });
  }

  private sort(incidents: EmergencyIncident[]): EmergencyIncident[] {
    return [...incidents].sort(
      (first, second) =>
        (DateUtil.getTimestampMs(second.activatedAt) ?? 0) -
        (DateUtil.getTimestampMs(first.activatedAt) ?? 0),
    );
  }

  private async resolveParticipantNames(
    organizationId: string,
    incidents: EmergencyIncident[],
  ): Promise<EmergencyIncident[]> {
    const participantIds = incidents.flatMap(incident => [
      ...(incident.subject.kind === 'linkedWorker' ? [incident.subject.userId] : []),
      ...(incident.response.primary ? [incident.response.primary.userId] : []),
      ...incident.response.support.map(responder => responder.userId),
    ]);
    const names = await this.participantService.resolveNames(organizationId, participantIds);

    return incidents.map(incident => ({
      ...incident,
      subject: {
        ...incident.subject,
        displayName:
          incident.subject.displayName ||
          (incident.subject.kind === 'linkedWorker' ? names[incident.subject.userId] : '') ||
          '',
      },
      response: {
        primary: incident.response.primary
          ? {
              ...incident.response.primary,
              displayName:
                incident.response.primary.displayName ||
                names[incident.response.primary.userId] ||
                '',
            }
          : null,
        support: incident.response.support.map(responder => ({
          ...responder,
          displayName: responder.displayName || names[responder.userId] || '',
        })),
      },
    }));
  }

  private getDetailCapabilitiesKey(
    record: EmergencyIncidentFirestoreRecord,
    assignments: EmergencyAssignmentFirestoreRecord[],
  ): string {
    const assignmentRoles = assignments
      .map(assignment => {
        const responderId =
          assignment.responder && typeof assignment.responder.id === 'string'
            ? assignment.responder.id
            : '';
        return {
          id: assignment.id,
          responderId,
          primary: Boolean(assignment.primary),
        };
      })
      .sort((first, second) => first.id.localeCompare(second.id));
    return JSON.stringify({
      state: record.state ?? '',
      divisionId: record.divisionId ?? '',
      primaryAssignmentId: record.primaryAssignmentId ?? '',
      assignments: assignmentRoles,
    });
  }

  private requireOrganization(organizationId: string): void {
    if (!organizationId) throw new Error('Organization ID is required');
  }

  private validateIncidentCommand(input: { organizationId: string; incidentId: string }): void {
    this.requireOrganization(input.organizationId);
    if (!input.incidentId) throw new Error('Incident ID is required');
  }
}
